/* eslint-disable @typescript-eslint/no-explicit-any */
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { updateAEMCQKeys, useEditorMode, Types } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { FC, lazy, Suspense, useEffect, startTransition, useState } from 'react';
import { VanilaCardCarouselContainer as StoryTilesContainerMolecule } from './VanilaCardCarouselContainer';
import { StoryTileProps, StoryTilesContainerProps, PublishCardWrapperProps } from './StoryTilesContainer.types';

export const StoryTilesContainerConfig = {
  emptyLabel: 'StoryTilesContainer',
  isEmpty: () => true,
  resourceType: `mi-aem-martech/components/lovetravels/storytiles`,
};

export const StoryTilesContainerComp: FC<StoryTilesContainerProps> = (props: StoryTilesContainerProps) => {
  const {
    headerText,
    subHeaderText,
    ctaLabel,
    ctaLink,
    eyebrow,
    ctaType,
    cardCount,
    openInaNewTab,
    totalNumberOfCards,
    trackingProperties,
    componentId,
    pagePath,
    itemPath,
    variation,
    enableTabletBreakpoint,
    isRTL = false,
    rtlEnable = false,
    comboHeaderTag,
  } = props;

  let { noOfCards, noOfCardsTablet } = props;
  const updatedProps = updateAEMCQKeys(props);
  const cqItems = updatedProps['cqItems'];
  const { isInEditor } = useEditorMode();
  const isCombo = (): boolean => {
    return variation === 'combo';
  };

  if (isCombo()) {
    noOfCards = 2;
    noOfCardsTablet = 2;
  }

  const isAuthorMode = AuthoringUtils.isInEditor();

  const mapper: Record<string, string> = {};
  mapper['cardvertical'] = 'CardVertical';
  mapper['cardlayered'] = 'CardLayered';

  useEffect(() => {
    const pageWcmMode = document.getElementsByTagName('html')[0];
    const updateStyleComponent = document.getElementById(`${componentId}__slides`);
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const currWcmMode = pageWcmMode?.getAttribute('class');
          if (currWcmMode?.includes('Edit')) {
            updateStyleComponent?.classList.add('glide__slides_authoring');
          } else if (currWcmMode?.includes('Preview')) {
            updateStyleComponent?.classList.remove('glide__slides_authoring');
          }
        }
      });
    });
    observer.observe(pageWcmMode, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, [componentId]);

  // This function is used to render the component in authoring mode authorCardWrapper
  const authorCardWrapper = (index: number) => {
    return (
      <ResponsiveGrid
        key={`${itemPath}/${totalNumberOfCards[index]}`}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${totalNumberOfCards[index]}`}
        columnCount="12"
        gridClassNames={''}
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-martech/components/container',
        }}
        isInEditor={isInEditor}
      />
    );
  };

  const FallbackComponent: FC = () => null;

  const getComponent = (moduleName: string) => {
    return lazy(async () => {
      const module: Record<string, any> = await import('@marriott/mi-ui-library');
      const Component = module[moduleName] || module['default'];
      return { default: Component ?? FallbackComponent };
    });
  };

  // This function is used to render the component in end-user mode
  const PublishCardWrapper: FC<PublishCardWrapperProps> = (props: PublishCardWrapperProps) => {
    const { cardName, jsonData } = props;
    // eslint-disable-next-line no-prototype-builtins
    const [Component, setComponent] = useState<React.LazyExoticComponent<any> | null>(null);
    const [componentProps, setComponentProps] = useState<any>(null);

    useEffect(() => {
      if (jsonData && Object.prototype.hasOwnProperty.call(jsonData, cardName)) {
        const card = jsonData[cardName];
        const cardItems = card[':items'];
        for (const itemKey in cardItems) {
          if (Object.prototype.hasOwnProperty.call(cardItems, itemKey)) {
            const item = cardItems[itemKey];
            const key = item?.componentId;
            const itemType = item[':type']?.split('/').pop();

            if (Object.prototype.hasOwnProperty.call(mapper, itemType)) {
              const innerComp = mapper[itemType];

              // ✅ Defer component creation until after hydration
              startTransition(() => {
                setComponent(getComponent(innerComp));
                setComponentProps({
                  ...item,
                  key,
                });
              });

              return; // Exit loop after scheduling update
            }
          }
        }
      }
    }, [cardName, jsonData]);

    if (!Component || !componentProps) return null;

    return (
      <li key={componentProps.key}>
        <Suspense fallback={<div style={{ minHeight: '20px' }}>Loading...</div>}>
          <Component {...componentProps} />
        </Suspense>
      </li>
    );
  };

  const customAttributes = trackingProperties?.enableScrollingBehavior ? { 'data-section-tracking': componentId } : {};

  return (
    <StoryTilesContainerMolecule
      componentId={componentId}
      subHeaderText={subHeaderText}
      styleclass={`lt-posts ${componentId}`}
      variation={variation}
      eyebrow={eyebrow}
      cardCount={cardCount}
      trackingProperties={trackingProperties}
      ctaLabel={ctaLabel}
      headerText={headerText}
      ctaLink={ctaLink}
      openInaNewTab={openInaNewTab}
      ctaType={ctaType}
      isCombo={isCombo}
      noOfCards={noOfCards}
      noOfCardsTablet={noOfCardsTablet}
      enableTabletBreakpoint={enableTabletBreakpoint}
      customAttributes={customAttributes}
      isDirectionRightToLeft={rtlEnable || isRTL}
      comboHeaderTag={comboHeaderTag}
      standardHeaderTag={`h2` as Types.tags.h2}
    >
      {isAuthorMode && Array.from({ length: totalNumberOfCards?.length }, (_, i) => authorCardWrapper(i))}

      {!isAuthorMode &&
        totalNumberOfCards?.map((cardName: string) => (
          <PublishCardWrapper key={cardName} cardName={cardName} jsonData={cqItems} />
        ))}
    </StoryTilesContainerMolecule>
  );
};

export const CarouselEditableComponent = (props: any) => {
  return (
    <EditableComponent config={StoryTilesContainerConfig} {...props}>
      <StoryTilesContainerComp {...props} componentName={props?.model?.cqType?.split('/').pop()} />
    </EditableComponent>
  );
};

export const StoryTilesContainer = (props: StoryTileProps) => {
  const { model } = props;

  if (model.variation === 'combo' && !model.styleclass?.includes('fullbleed')) {
    model.styleclass += ' fullbleed';
  }

  return (
    <div
      className={clsx({
        standard: model.styleclass?.includes('standard'),
        inverse: model.styleclass?.includes('inverse'),
        alternate: model.styleclass?.includes('alternate'),
        'm-container-fullbleed': model.styleclass?.includes('fullbleed'),
      })}
      data-testid="story-tiles"
      data-component-name="o-prearrival-storytilescontainer"
      data-target-component={model?.targetComponentName || null}
    >
      <div className={clsx('container', { '': !model.styleclass?.includes('fullbleed') })}>
        <CarouselEditableComponent {...props} />
      </div>
    </div>
  );
};
